import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

export interface QueryParamHeaders {
  [key: string]: string
}

/**
 * Hook to capture URL query parameters and manage them as potential API headers
 * Example: /rentaloptions?nocache=1 -> { nocache: '1' }
 */
export function useQueryParams() {
  const location = useLocation()
  const [queryParams, setQueryParams] = useState<QueryParamHeaders>({})

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const params: QueryParamHeaders = {}

    // Convert all query parameters to key-value pairs
    searchParams.forEach((value, key) => {
      params[key] = value
    })

    // Create/clear NoCacheMode cookie based on nocache query param so it works before login
    try {
      const nocacheRaw = params['nocache']
      if (typeof document !== 'undefined' && typeof nocacheRaw !== 'undefined') {
        const val = String(nocacheRaw).toLowerCase()
        if (val === '1' || val === 'true') {
          const expires = new Date(Date.now() + 1 * 60 * 60 * 1000).toUTCString() // 1 hour
          document.cookie = `NoCacheMode=1; Path=/; SameSite=Lax; Expires=${expires}`
        } else if (val === '0' || val === 'false') {
          document.cookie = `NoCacheMode=; Path=/; SameSite=Lax; Expires=Thu, 01 Jan 1970 00:00:00 GMT`
        }
      }
    } catch {}

    setQueryParams(params)
  }, [location.search])

  return queryParams
}

/**
 * Hook to get specific query parameters that should be included as API headers
 * You can customize this list based on your needs
 */
export function useApiHeaders() {
  const queryParams = useQueryParams()
  
  // Define which query parameters should be passed as headers
  const headerParams = ['nocache', 'debug', 'cache-control', 'force-refresh']
  
  const apiHeaders: QueryParamHeaders = {}
  
  headerParams.forEach(param => {
    if (queryParams[param]) {
      // Convert parameter names to header format (kebab-case with X- prefix)
      const headerName = `X-${param}`
      apiHeaders[headerName] = queryParams[param]
    }
  })

  return apiHeaders
} 